// The single AI panel of the new interface (ui_v2, REDISENO_UI.md 3.7, C18).
//
// One docked panel on the right, 400 px, for every page: a header with the AI
// mark, the modes the page offers as tabs (General is the platform assistant;
// the Workbench adds Analista and Cuaderno, Logic Studio its Copilot, the
// process map its chat), and the context that mode sees as chips. A page mode
// renders its own component into the slot this panel gives it (see
// assistantModes.ts), so each backend, stream and context stays as it was.
//
// Without the flag the shell draws NexusAssistant as before and never mounts
// this file.
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Bot, BrainCircuit, MessageSquare, ScanSearch, Sparkles, X } from 'lucide-react';
import NexusAssistant from './NexusAssistant';
import { Tabs } from '../design-system/components/Tabs';
import { useAssistantStore } from '../store/assistantStore';
import { useNavigationStore } from '../store/navigationStore';
import { NAV_ITEMS } from './GroupedNavRail';
import { navItemFor } from './routes';
import {
  GENERAL_MODE, effectiveMode, toggleAssistantPanel, useAssistantModes,
  type AssistantModeIcon,
} from './assistantModes';
import './assistant.css';

const MODE_ICONS: Record<AssistantModeIcon | 'general', React.ReactNode> = {
  general: <MessageSquare size={14} />,
  analyst: <Bot size={14} />,
  notebook: <BookOpen size={14} />,
  copilot: <BrainCircuit size={14} />,
  map: <ScanSearch size={14} />,
};

/** A page mode's DOM node: the page portals its component in here. */
const ModeSlot: React.FC<{ id: string; visible: boolean }> = ({ id, visible }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const setSlot = useAssistantModes((s) => s.setSlot);
  useEffect(() => {
    setSlot(id, ref.current);
    return () => setSlot(id, null);
  }, [id, setSlot]);
  return <div ref={ref} className="as-mode" data-mode={id} hidden={!visible} />;
};

/** ⌘J / Ctrl+J opens and closes the assistant (Cmd+K stays the search). */
export function useAssistantShortcut() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      if (e.key.toLowerCase() !== 'j') return;
      e.preventDefault();
      toggleAssistantPanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}

export const AssistantPanel: React.FC = () => {
  const { t } = useTranslation();
  const open = useAssistantStore((s) => s.open);
  const setOpen = useAssistantStore((s) => s.setOpen);
  const modes = useAssistantModes((s) => s.modes);
  const mode = useAssistantModes(effectiveMode);
  const setMode = useAssistantModes((s) => s.setMode);
  const currentPage = useNavigationStore((s) => s.currentPage);
  useAssistantShortcut();

  if (!open) return null;

  const navItem = NAV_ITEMS.find((i) => i.path === navItemFor(currentPage));
  const pageLabel = navItem ? t(navItem.i18nKey, navItem.label) : currentPage;
  const activeDef = modes.find((m) => m.id === mode);
  // Chips name only what the mode really sends: the page for General (it
  // reads the current page and its focused records), the page and the open
  // resource for a page mode.
  const chips = [pageLabel, ...(activeDef?.context || [])].filter(Boolean);
  const tabs = [
    { id: GENERAL_MODE, label: t('shell.assistant.v2.general', 'General'), icon: MODE_ICONS.general },
    ...modes.map((m) => ({ id: m.id, label: m.label, icon: MODE_ICONS[m.icon] })),
  ];

  return (
    <aside className="as-panel" data-testid="assistant-panel" data-tour-skip aria-label={t('shell.assistant.v2.title', 'Asistente')}>
      <div className="as-head">
        <Sparkles size={16} className="as-ai-icon" aria-hidden />
        <span className="as-head__title">{t('shell.assistant.v2.title', 'Asistente')}</span>
        <span className="as-ai-chip">{t('shell.assistant.v2.aiChip', 'IA')}</span>
        <span className="as-head__spacer" />
        <kbd className="as-kbd" title={t('shell.assistant.v2.shortcut', 'Abrir y cerrar el asistente')}>⌘J</kbd>
        <button type="button" className="as-icon-btn" onClick={() => setOpen(false)}
          title={t('common.close', 'Cerrar')} aria-label={t('common.close', 'Cerrar')}>
          <X size={14} />
        </button>
      </div>

      {modes.length > 0 && (
        <div className="as-tabs">
          <Tabs tabs={tabs} value={mode} onChange={setMode} aria-label={t('shell.assistant.v2.modes', 'Modos del asistente')} />
        </div>
      )}

      <div className="as-context" data-testid="assistant-context">
        <span className="as-context__label">{t('shell.assistant.v2.context', 'Contexto')}</span>
        {chips.map((c, i) => <span key={`${i}-${c}`} className="as-chip" title={c}>{c}</span>)}
      </div>

      <div className="as-body">
        <div className="as-mode" data-mode={GENERAL_MODE} hidden={mode !== GENERAL_MODE}>
          <NexusAssistant embedded />
        </div>
        {modes.map((m) => <ModeSlot key={m.id} id={m.id} visible={mode === m.id} />)}
      </div>
    </aside>
  );
};

export default AssistantPanel;
